/**
 * Operator Profile Page
 * Personal cabinet for the operator
 */

import { useEffect, useState } from 'react';
import { 
  User, 
  Shield,
  Mail,
  Calendar,
  Activity,
  LogOut,
  Key,
  Sun,
  Moon,
  Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { format } from 'date-fns';

export function SettingsPage() {
  const { user, logout } = useAuthStore();
  const { mode, setMode } = useThemeStore();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleChangePassword = () => {
    toast.info('Password change coming soon', {
      description: 'This feature will be available in a future update',
    });
  };

  // Get role display info
  const getRoleInfo = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return { label: 'Administrator', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
      case 'MANAGER':
        return { label: 'Manager', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
      case 'OPERATOR':
      default:
        return { label: 'Operator', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
    }
  };

  const roleInfo = getRoleInfo(user?.role || 'OPERATOR');

  // Theme options
  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun, description: 'Light mode' },
    { value: 'dark' as const, label: 'Dark', icon: Moon, description: 'Dark mode' },
    { value: 'system' as const, label: 'System', icon: Monitor, description: 'Follow system settings' },
  ];

  return (
    <div className="min-h-full bg-slate-100 dark:bg-slate-900 p-6 transition-colors">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-600/20 rounded-lg border border-blue-200 dark:border-blue-500/30">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            OPERATOR PROFILE
          </h1>
          <p className="text-slate-500 font-mono mt-2 text-sm">
            Personal cabinet // Access level: {roleInfo.label}
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden mb-6 shadow-sm dark:shadow-none">
          {/* Profile Header */}
          <div className="bg-slate-50 dark:bg-slate-800 px-6 py-8 border-b border-slate-200 dark:border-slate-700/50">
            <div className="flex items-center gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center border-2 border-blue-400/50 shadow-lg shadow-blue-500/20">
                <span className="text-3xl font-mono font-bold text-white">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'OP'}
                </span>
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100">
                  {user?.name || 'Operator'}
                </h2>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`px-3 py-1 text-xs font-mono font-medium uppercase tracking-wider rounded border ${roleInfo.color}`}>
                    <Shield className="w-3 h-3 inline mr-1" />
                    {roleInfo.label}
                  </span>
                  <span className="px-3 py-1 text-xs font-mono font-medium bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-500/30 rounded uppercase tracking-wider">
                    <Activity className="w-3 h-3 inline mr-1" />
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="p-6 space-y-4">
            {/* Email */}
            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Email</p>
                  <p className="font-mono text-slate-700 dark:text-slate-200">{user?.email || 'operator@gridinspect.local'}</p>
                </div>
              </div>
            </div>

            {/* User ID */}
            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">User ID</p>
                  <p className="font-mono text-slate-500 dark:text-slate-400 text-sm">{user?.id?.substring(0, 8) || '--------'}...</p>
                </div>
              </div>
            </div>

            {/* Last Login */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                <div>
                  <p className="text-xs font-mono text-slate-500 uppercase tracking-wider">Session Started</p>
                  <p className="font-mono text-slate-700 dark:text-slate-200">{format(new Date(), 'yyyy.MM.dd // HH:mm:ss')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 mb-6 shadow-sm dark:shadow-none">
          <h3 className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wider">
            Appearance
          </h3>
          
          <div className="grid grid-cols-3 gap-3">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = mode === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => setMode(option.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20'
                      : 'border-slate-200 dark:border-slate-600/50 bg-slate-50 dark:bg-slate-700/30 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${
                    isActive 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-slate-400 dark:text-slate-500'
                  }`} />
                  <span className={`font-mono text-sm font-medium ${
                    isActive 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    {option.label}
                  </span>
                  <span className={`text-xs font-mono ${
                    isActive 
                      ? 'text-blue-500 dark:text-blue-300' 
                      : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/50 p-6 shadow-sm dark:shadow-none">
          <h3 className="text-sm font-mono font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wider">
            Account Actions
          </h3>
          
          <div className="space-y-3">
            {/* Change Password */}
            <button
              onClick={handleChangePassword}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
                <div className="text-left">
                  <p className="font-mono text-slate-700 dark:text-slate-200">Change Password</p>
                  <p className="text-xs font-mono text-slate-500">Update your account password</p>
                </div>
              </div>
              <span className="text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-400">&rarr;</span>
            </button>

            {/* Logout */}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center justify-between px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-500/30 transition-colors group disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5 text-red-500 dark:text-red-400" />
                <div className="text-left">
                  <p className="font-mono text-red-600 dark:text-red-400">Logout</p>
                  <p className="text-xs font-mono text-slate-500">End current session</p>
                </div>
              </div>
              {isLoggingOut ? (
                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="text-red-400 group-hover:text-red-500 dark:text-red-500 dark:group-hover:text-red-400">&rarr;</span>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="font-mono text-xs text-slate-500 dark:text-slate-600">
            GRIDINSPECT PORTAL v3.0.0 // ELIFLY &copy; 2026
          </p>
        </div>
      </div>
    </div>
  );
}
